import { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { isSsoConfigured, loginRequest, isSamlConfigured, getSamlLoginUrl, isEmailAuthEnabled } from '@/auth/config';
import { isBlockedEmailDomain, BLOCKED_EMAIL_MESSAGE } from '@/auth/blockedEmailDomains';
import { setStoredEmailToken } from '@/auth/emailAuth';
import { useAuth } from '@/auth/useAuth';
import { apiPostNoAuth } from '@/api/client';
import Turnstile, { isCaptchaEnabled } from '@/components/Turnstile';
import logoUrl from '@/assets/silisense-logo.png';

export default function Login() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const ssoConfigured = isSsoConfigured();
  const samlConfigured = isSamlConfigured();
  const emailAuthEnabled = isEmailAuthEnabled();

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const handleSSO = () => {
    if (!ssoConfigured) return;
    setError('');
    instance.loginRedirect(loginRequest).catch((e) => console.error(e));
  };

  const handleSaml = () => {
    if (!samlConfigured) return;
    setError('');
    window.location.href = getSamlLoginUrl();
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password) {
      setError('Please enter your email and password.');
      return;
    }
    if (isBlockedEmailDomain(trimmed)) {
      setError(BLOCKED_EMAIL_MESSAGE);
      return;
    }
    if (isCaptchaEnabled() && !captchaToken) {
      setError('Please complete the CAPTCHA challenge.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiPostNoAuth<{ accessToken: string }>('/v1/auth/login', {
        email: trimmed,
        password,
        captchaToken,
      });
      if (res?.accessToken) {
        setStoredEmailToken(res.accessToken);
        navigate('/dashboard', { replace: true });
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Sign-in failed. Check your email and password.';
      const msg =
        raw === 'Failed to fetch' || raw.toLowerCase().includes('fetch')
          ? 'SERVER_UNREACHABLE' // Shown as short hint when SSO available
          : raw;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const showSso = ssoConfigured;
  const showSaml = samlConfigured;
  const noMethod = !showSso && !showSaml && !emailAuthEnabled;

  return (
    <div className="login-page">
      <div className="login-card">
        <img
          src={logoUrl}
          alt="SILISENSE — Academic Integrity Reimagined"
          className="login-logo"
          width={280}
          height={90}
          decoding="async"
        />
        <p className="login-subtitle">Anti-plagiarism & AI-content detection</p>
        <p className="login-domain">silisense.digital</p>

        {noMethod && (
          <div className="login-error" role="alert">
            <strong>No sign-in method configured.</strong> Set at least one of: <code>VITE_SSO_CLIENT_ID</code> (Microsoft), <code>VITE_SAML_LOGIN_URL</code>, or <code>VITE_EMAIL_AUTH_ENABLED=true</code> in your <code>.env</code>.
          </div>
        )}

        {!ssoConfigured && (showSaml || emailAuthEnabled) && (
          <p className="login-hint">Microsoft SSO is not configured; use SAML or email sign-in below.</p>
        )}

        {error && (
          <div
            className={
              error === 'SERVER_UNREACHABLE' && showSso ? 'login-hint login-hint-dismissible' : 'login-error'
            }
            role="alert"
          >
            <span className={error === 'SERVER_UNREACHABLE' && showSso ? '' : 'login-error-text'}>
              {error === 'SERVER_UNREACHABLE'
                ? showSso
                  ? 'Email sign-in cannot reach the server. Use Sign in with Microsoft (SSO) above, or set VITE_API_BASE_URL to your Azure API URL and redeploy the webapp.'
                  : 'Cannot reach the server. Set VITE_API_BASE_URL in config.env to your Azure API URL and redeploy the webapp.'
                : error}
            </span>
            <button
              type="button"
              className="login-error-dismiss"
              onClick={() => setError('')}
              aria-label="Dismiss"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {showSso && (
          <button type="button" onClick={handleSSO} className="btn-primary btn-login" disabled={!ssoConfigured}>
            Sign in with Microsoft (SSO)
          </button>
        )}

        {showSaml && (
          <button type="button" onClick={handleSaml} className="btn-primary btn-login btn-login-secondary">
            Sign in with SAML SSO
          </button>
        )}

        {/* Email/password form — always visible so users can sign in without SSO */}
        <p className="login-divider" role="presentation">
          {showSso || showSaml ? 'Or sign in with email' : 'Sign in with email'}
        </p>
        <form onSubmit={handleEmailSubmit} className="login-form" aria-label="Email and password sign-in">
          <div className="form-group">
            <label htmlFor="login-email">Work email</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="form-input"
              disabled={loading}
            />
            <p className="form-hint">Company or institution email only (no Gmail, Yahoo, etc.)</p>
          </div>
              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <div className="password-input-wrap">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
          <Turnstile onToken={setCaptchaToken} />
          <button type="submit" className="btn-primary btn-login" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in with email'}
          </button>
        </form>
        <p className="login-hint">
          Don&apos;t have an account? <Link to="/signup">Sign up</Link> with your company email.
        </p>

        {showSso && (
          <p className="login-hint">Use your organisation account for single sign-on.</p>
        )}
      </div>
    </div>
  );
}
